import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GetQuoteForProduct from "@tSurya/Components/GetQuoteForProduct";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => ({
    isAuthenticated: state.context.session.isAuthenticated,
});
interface OwnProps {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    selectedIndex?: number;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
}

type Props = ReturnType<typeof mapStateToProps> & HasProduct & OwnProps;

export interface ProductDetailsVariantButtonStyles {
    wrapper?: InjectableCss;
    swatchHeader?: InjectableCss;
    button?: ButtonPresentationProps;
    labelText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    selectedButton?: InjectableCss;
    buttonNormal?: InjectableCss;
    customModal?: ModalPresentationProps;
}

export const variantButtonsStyles: ProductDetailsVariantButtonStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
            margin-bottom:6px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 24px;
                    `,
                    css`
                        margin-top: 24px;
                    `,
                    css`
                        margin-top: 24px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    swatchHeader: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },
    button: {
        css: css`
            align-items: center;
            padding: 8px 15px !important;
            border: 1px solid #E0E0E0;
            margin-right: 12px;
            margin-bottom: 10px;
            border-radius: 0px;
            background-color: #fff !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 8px 10px !important;
                    `,
                    css`
                        padding: 8px 10px !important;
                    `,
                    css`
                        padding: 8px 10px !important;
                    `,
                    null,
                    null,
                ])};
            /* text-transform: uppercase; */
            &:hover {
                background: ${getColor("secondary")};
                background-color: #ffffff !important;
            }
            &:focus {
                background-color: ${getColor("common.backgroundContrast")} !important;
                outline: none;
            }
            &:active {
                background-color: ${getColor("common.backgroundContrast")} !important;
                outline: none;
            }
            &.activeClass {
                background-color: #f8f8f8 !important;
                cursor:auto;
                border: 2px solid #323232;
                span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.weight.base};
                    font-size: 15px;
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                    text-align: center;
                    color: ${COLORS.text.main};
                }
            }
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.base};
                font-size: 15px;
                line-height: ${TYPOGRAPHY.lineHeight.base};
                text-align: center;
                color: ${COLORS.text.main};
                //text-transform: lowercase;
            }
        `,
        // hoverAnimation: "grow",
    },
    labelText: {
        css: css`
            display: inline-block;
            width: 100%;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${getColor("text.main")};
            margin-bottom: 10px;
        `,
    },
    link: {
        css: css`
            margin: 5px;
            white-space: nowrap;

            //display: none;
            > span {
                color: ${getColor("text.link")} !important;
                &:hover {
                    /* color: #5e4a40; */
                }
            }

            display: none;
        `,
    },
    customModal: {
        sizeVariant: "large",
    },
};

const styles = variantButtonsStyles;

const ProductDetailsVariantButton = ({
    variantSelection,
    updateVariantSelection,
    variantTrait,
    selectedIndex,
    product,
    isAuthenticated,
}: Props) => {
    const [isActive, setIsActive] = useState(false);
    useEffect(() => {
        if(variantTrait.traitValues?.length === 1){
            updateVariantSelection(variantTrait.traitValues[0].id, variantTrait.id)
        }
    }, []);
    return (
        <>
            <StyledWrapper {...styles.wrapper} key={variantTrait.id}>
                <StyledWrapper {...styles.swatchHeader}>
                    <Typography {...styles.labelText}>{variantTrait.traitValues?.length === 1 ? "Size" : variantTrait.nameDisplay}</Typography>
                    <Link
                        {...styles.link}
                        onClick={() => {
                            updateVariantSelection("", variantTrait.id);
                            setIsActive(true);
                        }}
                    >
                        {translate("Clear Selection")}
                    </Link>
                </StyledWrapper>
                {variantTrait.traitValues?.map((traitValue, index) => (
                    <Button
                        {...styles.button}
                        className={variantSelection[variantTrait.id] === traitValue.id ? "activeClass" : ""}
                        variant={variantSelection[variantTrait.id] === traitValue.id ? "primary" : "secondary"}
                        buttonType={variantSelection[variantTrait.id] === traitValue.id ? "solid" : "outline"}
                        value={`${traitValue.id}`}
                        key={`${traitValue.id}`}
                        disabled={traitValue.isDisabled}
                        onClick={event => updateVariantSelection(event.currentTarget.value, variantTrait.id)}
                    >
                        {traitValue.valueDisplay}
                    </Button>
                ))}
            </StyledWrapper>
        </>
    );
};

export default withProduct(connect(mapStateToProps)(ProductDetailsVariantButton));
